import { useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Download, FileLock2, Loader2, ScrollText, ShieldQuestion } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import {
  fetchUser,
  fetchUserWallet,
  fetchUserStrikes,
} from '@/lib/adapters'
import { CardBackLink, ListBackLink, Body, ActionCard, ReasonInput } from './userActionKit'
import {
  USERS_STR as S,
  fetchPiiAccessLog,
  requestMessageAccess,
  isPending,
  type PiiAccessRow,
} from './usersAdminApi'
import { fetchLoginHistory } from './loginHistoryApi'
import { fetchUserNotes } from './userNotesApi'
import { fetchUserWarnings } from './userWarningsApi'

/**
 * A3.23 — PII kirish jurnali. JONLI `GET /admin/audit/pii` (superadmin-only:
 * shaxsiy ma'lumotни kim o'qigani JURNALI o'zi ham maxfiy). Kim (admin), qachon
 * (vaqt), nima (toifa/obyekt) ko'rgani. 403 → PermissionDenied, read-only.
 */
export function PiiAccessLog() {
  const columns = useMemo<AdminColumn<PiiAccessRow>[]>(
    () => [
      { key: 'created_at', header: 'Vaqt', type: 'date', width: 160 },
      { key: 'admin', header: S.piiAdmin, type: 'text', width: 160, accessor: (r) => r.admin ?? '—' },
      { key: 'category', header: S.piiCat, type: 'text', width: 150 },
      { key: 'target', header: S.piiSubject, type: 'text', minWidth: 180, accessor: (r) => r.target ?? '—' },
      { key: 'detail', header: 'Izoh', type: 'longtext', minWidth: 200, accessor: (r) => r.detail ?? '—' },
      { key: 'ip', header: 'IP', type: 'text', width: 130, accessor: (r) => r.ip ?? '—' },
    ],
    [],
  )
  const fetcher = useCallback(fetchPiiAccessLog, [])
  return (
    <>
      <PageHeader title={S.piiTitle} screenId="A3.23" actions={<ListBackLink />} />
      <DataTable<PiiAccessRow>
        screenId="A3.23"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <ScrollText size={28} strokeWidth={1.5} />, description: S.piiHint }}
      />
    </>
  )
}

/**
 * A3.24 — Ma'lumot eksporti (GDPR so'rovi bo'yicha). Foydalanuvchining barcha
 * ma'lumotini JONLI endpointlardan yig'ib bitta JSON faylга yozadi (karta,
 * hamyon, kirish tarixi, strayk, ogohlantirish, ichki izohlar). Faylga
 * yig'ilishining o'zi PII kirish — serverда A3.23 jurnaliga tushadi.
 */
export function UserDataExport() {
  const { id = '' } = useParams()
  const userId = Number(id)
  const { toast } = useToast()
  const [busy, setBusy] = useState(false)

  const build = async () => {
    setBusy(true)
    try {
      const [user, wallet, logins, strikes, warnings, notes] = await Promise.all([
        fetchUser(userId).catch(() => null),
        fetchUserWallet(userId).catch(() => null),
        fetchLoginHistory(userId).catch(() => []),
        fetchUserStrikes(userId).catch(() => ({ strikes: [], active_points: 0 })),
        fetchUserWarnings(userId).catch(() => []),
        fetchUserNotes(userId).catch(() => []),
      ])
      const bundle = {
        exported_at: new Date().toISOString(),
        user_id: userId,
        profile: user,
        wallet,
        login_history: logins,
        strikes,
        warnings,
        internal_notes: notes,
      }
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `grom-user-${userId}-gdpr-${new Date().toISOString().slice(0, 10)}.json`
      a.click()
      URL.revokeObjectURL(url)
      toast(S.done, { tone: 'success' })
    } catch (e) {
      toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader title={S.gdprTitle} screenId="A3.24" actions={<CardBackLink id={id} />} />
      <Body>
        <ActionCard hint={S.gdprHint}>
          <button
            onClick={() => void build()}
            disabled={busy || !userId}
            className="inline-flex items-center gap-1.5 rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {busy ? S.gdprBuilding : S.gdprBtn}
          </button>
        </ActionCard>
      </Body>
    </>
  )
}

/**
 * A3.25 — Xabarlarni ko'rish (L4 — huquqiy so'rov + four-eyes). ENG maxfiy PII
 * darajasi. Xabar KONTENTI bu ekranда KO'RSATILMAYDI — huquqiy asos bilan kirish
 * SO'ROVI yaratiladi, u four-eyes tasdig'iga boradi; grant qilingan kirish A3.23
 * jurnaliga tushadi. Greenfield → 404 bo'lsa «kutilmoqda».
 */
export function UserMessageView() {
  const { id = '' } = useParams()
  const userId = Number(id)
  const { toast } = useToast()
  const [legalRef, setLegalRef] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [requested, setRequested] = useState(false)

  const canSubmit = legalRef.trim().length >= 3 && reason.trim().length >= 3 && !busy

  const submit = async () => {
    if (!canSubmit) return
    setBusy(true)
    try {
      await requestMessageAccess(userId, legalRef, reason.trim())
      setRequested(true)
      toast(S.msgRequested, { tone: 'success' })
    } catch (e) {
      if (isPending(e)) toast(S.pending, { tone: 'warning' })
      else toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader title={S.msgTitle} screenId="A3.25" actions={<CardBackLink id={id} />} />
      <Body>
        <ActionCard hint={S.msgHint}>
          <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
            <FileLock2 size={14} className="mt-0.5 shrink-0" />
            <span>L4 — four-eyes tasdig'i talab qilinadi. Kontent hech qachon bu yerда ochilmaydi.</span>
          </div>
          <label className="block">
            <span className="mb-1 block text-xs text-tertiary">{S.msgLegalRef}</span>
            <input
              value={legalRef}
              onChange={(e) => setLegalRef(e.target.value)}
              placeholder={S.msgLegalPh}
              className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-1.5 text-sm text-primary outline-none focus:border-border-strong"
            />
          </label>
          <ReasonInput value={reason} onChange={setReason} rows={3} />
          <button
            onClick={() => void submit()}
            disabled={!canSubmit || requested}
            className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/50 bg-amber-500/10 px-4 py-1.5 text-sm font-medium text-amber-300 hover:bg-amber-500/20 disabled:opacity-50"
          >
            <ShieldQuestion size={14} />
            {requested ? S.msgRequested : S.msgRequest}
          </button>
        </ActionCard>
      </Body>
    </>
  )
}
